/**
 * @fileoverview React hook for formatting dates with tenant timezone
 * All dates are stored in UTC and converted to tenant timezone for display
 */
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook for formatting dates in tenant's timezone
 * @returns {Object} - Formatting functions
 */
export function useDateFormat() {
    const { user } = useAuth();

    // Read timezone from context user, fallback to localStorage (fresher after /api/auth/me sync),
    // then default to UTC
    const getTimezone = () => {
        if (user?.tenant?.timezone) return user.tenant.timezone;
        try {
            const stored = localStorage.getItem('user');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.tenant?.timezone) return parsed.tenant.timezone;
            }
        } catch (_) { /* ignore */ }
        return 'UTC';
    };
    const tenantTimezone = getTimezone();

    /**
     * Format a UTC date in the tenant's timezone
     * @param {string|Date} utcDate - UTC date from database
     * @param {string} format - Format type: 'short', 'medium', 'full', 'time'
     * @returns {string} - Formatted date string
     */
    const formatDate = (utcDate, format = 'full') => {
        if (!utcDate) return '';

        try {
            const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;

            // Invalid date check
            if (isNaN(date.getTime())) return '';

            const options = {
                timeZone: tenantTimezone,
                hour12: false // 24-hour format
            };

            switch (format) {
                case 'short':
                    // 09/02/2026
                    return new Intl.DateTimeFormat('es-AR', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).format(date);

                case 'medium':
                    // 09/02/2026 20:30
                    return new Intl.DateTimeFormat('es-AR', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    }).format(date);

                case 'full':
                    // 09/02/2026 20:30:45
                    return new Intl.DateTimeFormat('es-AR', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).format(date);

                case 'time':
                    // 20:30:45
                    return new Intl.DateTimeFormat('es-AR', {
                        ...options,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).format(date);

                default:
                    return new Intl.DateTimeFormat('es-AR', {
                        ...options,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).format(date);
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    /**
     * Get current time in tenant timezone
     * @returns {string} - Current time formatted
     */
    const getCurrentTime = () => {
        return formatDate(new Date(), 'full');
    };

    /**
     * Get timezone offset string
     * @returns {string} - Offset (e.g., 'UTC-03:00')
     */
    const getTimezoneOffset = () => {
        try {
            // Use Intl.DateTimeFormat with timeZoneName to get the offset reliably
            const formatter = new Intl.DateTimeFormat('en', {
                timeZone: tenantTimezone,
                timeZoneName: 'shortOffset'
            });
            const parts = formatter.formatToParts(new Date());
            const offsetPart = parts.find(p => p.type === 'timeZoneName');
            if (offsetPart) {
                // offsetPart.value is like "GMT-3" or "GMT+5:30" — convert to "UTC-03:00"
                const raw = offsetPart.value.replace('GMT', '');
                if (!raw || raw === '+0' || raw === '-0') return 'UTC+00:00';
                const sign = raw[0];
                const rest = raw.slice(1);
                const [h, m = '0'] = rest.split(':');
                return `UTC${sign}${String(parseInt(h)).padStart(2, '0')}:${String(parseInt(m)).padStart(2, '0')}`;
            }
            return tenantTimezone;
        } catch (error) {
            return 'UTC';
        }
    };

    return {
        formatDate,
        getCurrentTime,
        getTimezoneOffset,
        tenantTimezone
    };
}
